import React, { useState, useEffect, useRef } from 'react';
import { bloggerApi, bloggerAuth } from '../services/api';

function errMsg(e, f) { return e?.response?.data?.error || f; }

// 博主端共用登录/注册组件（站内只用密码登录；注册设密码以便券宝宝 App 登录）。
// onDone(needProfile) — needProfile 为真表示还没填昵称，调用方可跳资料页。
export default function BloggerLogin({ onDone }) {
  const [mode, setMode] = useState('login'); // login | register
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [code, setCode] = useState('');
  const [status, setStatus] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearInterval(timer.current), []);

  const switchMode = (m) => { setMode(m); setStatus(''); setPassword(''); setConfirm(''); setCode(''); };

  // 澳洲手机号统一存成 +61 开头；用户只输后面部分，去掉开头的 0。
  const fullPhone = () => '+61' + phone.replace(/^0+/, '');

  const sendCode = async () => {
    if (!phone) { setStatus('请输入手机号'); return; }
    setStatus(''); setBusy(true);
    try {
      await bloggerApi.sendCode(fullPhone());
      setCountdown(60);
      timer.current = setInterval(() => setCountdown((n) => { if (n <= 1) { clearInterval(timer.current); return 0; } return n - 1; }), 1000);
      setStatus('验证码已发送');
    } catch (e) { setStatus(errMsg(e, '验证码发送失败')); } finally { setBusy(false); }
  };

  const login = async () => {
    if (!phone || !password) { setStatus('请输入手机号和密码'); return; }
    setStatus(''); setBusy(true);
    try {
      const d = await bloggerApi.login(fullPhone(), password);
      bloggerAuth.save({ token: d.token, phone: d.phone });
      onDone(!!d.needProfile);
    } catch (e) { setStatus(errMsg(e, '登录失败')); } finally { setBusy(false); }
  };

  const register = async () => {
    if (!phone || !code.trim()) { setStatus('请输入手机号和验证码'); return; }
    if (!password) { setStatus('请设置密码'); return; }
    if (password !== confirm) { setStatus('两次密码不一致'); return; }
    setStatus(''); setBusy(true);
    try {
      const d = await bloggerApi.register(fullPhone(), password, confirm, code.trim());
      bloggerAuth.save({ token: d.token, phone: d.phone });
      onDone(!!d.needProfile);
    } catch (e) { setStatus(errMsg(e, '注册失败')); } finally { setBusy(false); }
  };

  return (
    <div style={s.scroll}>
      <div style={s.card}>
        <div style={s.tabs}>
          <button style={{ ...s.tab, ...(mode === 'login' ? s.tabOn : {}) }} onClick={() => switchMode('login')}>登录</button>
          <button style={{ ...s.tab, ...(mode === 'register' ? s.tabOn : {}) }} onClick={() => switchMode('register')}>注册</button>
        </div>

        <div style={s.phoneRow}>
          <span style={s.prefix}>+61</span>
          <input style={s.phoneInput} type="tel" inputMode="numeric" placeholder="手机号（不含开头的 0）"
            value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))} />
        </div>

        {mode === 'register' && (
          <div style={s.codeRow}>
            <input style={{ ...s.input, flex: 1, marginBottom: 0 }} type="tel" inputMode="numeric" maxLength={6}
              placeholder="验证码" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
            <button style={{ ...s.codeBtn, ...(countdown || busy ? s.disabled : {}) }}
              onClick={sendCode} disabled={countdown > 0 || busy}>{countdown > 0 ? `${countdown}s` : '获取验证码'}</button>
          </div>
        )}

        <input style={s.input} type="password" placeholder={mode === 'register' ? '设置密码' : '请输入密码'}
          value={password} onChange={(e) => setPassword(e.target.value)} />

        {mode === 'register' && (
          <input style={s.input} type="password" placeholder="确认密码"
            value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        )}

        {status && <p style={s.msgText}>{status}</p>}

        {mode === 'login' ? (
          <button style={{ ...s.primaryBtn, ...(busy ? s.disabled : {}) }} onClick={login} disabled={busy}>登录</button>
        ) : (
          <button style={{ ...s.primaryBtn, ...(busy ? s.disabled : {}) }} onClick={register} disabled={busy}>注册</button>
        )}

        {mode === 'login'
          ? <p style={s.tip}>没有密码？如果之前用验证码登录过，请到券宝宝 App 点「忘记密码」用手机号设置后再登录。</p>
          : <p style={s.tip}>注册后同一手机号和密码即可登录券宝宝 App。手机号请与 App 端保持一致。</p>}
      </div>
    </div>
  );
}

const s = {
  scroll: { padding: 16 },
  card: { background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #f0f0f0' },
  tabs: { display: 'flex', gap: 8, marginBottom: 16 },
  tab: { flex: 1, padding: '10px 0', borderRadius: 8, border: '1px solid #eee', background: '#fafafa', color: '#666', fontSize: 15 },
  tabOn: { background: '#FA1C51', borderColor: '#FA1C51', color: '#fff', fontWeight: 600 },
  input: { width: '100%', boxSizing: 'border-box', padding: '12px 14px', borderRadius: 8, border: '1px solid #e5e5e5', fontSize: 15, marginBottom: 12, outline: 'none' },
  phoneRow: { display: 'flex', alignItems: 'stretch', border: '1px solid #e5e5e5', borderRadius: 8, marginBottom: 12, overflow: 'hidden' },
  prefix: { display: 'flex', alignItems: 'center', padding: '0 12px', background: '#f5f5f5', color: '#333', fontSize: 15, borderRight: '1px solid #e5e5e5' },
  phoneInput: { flex: 1, boxSizing: 'border-box', padding: '12px 14px', border: 'none', fontSize: 15, outline: 'none' },
  codeRow: { display: 'flex', gap: 8, marginBottom: 12 },
  codeBtn: { padding: '0 14px', borderRadius: 8, border: '1px solid #FA1C51', background: '#fff', color: '#FA1C51', fontSize: 14, whiteSpace: 'nowrap' },
  primaryBtn: { width: '100%', padding: '13px 0', borderRadius: 8, border: 'none', background: '#FA1C51', color: '#fff', fontSize: 16, fontWeight: 600, marginTop: 4 },
  disabled: { opacity: 0.5 },
  msgText: { color: '#FA1C51', fontSize: 13, margin: '0 0 10px' },
  tip: { color: '#999', fontSize: 12, lineHeight: 1.6, margin: '14px 0 0' },
};
